import logo from "../images/modon.png";

/* ── i18n helpers ────────────────────────────────────────────── */
function getLang() {
  return localStorage.getItem("cma_language") || "en";
}
function tr(en, ar) {
  return getLang() === "ar" ? ar : en;
}
function dir() {
  return getLang() === "ar" ? "rtl" : "ltr";
}
/* Null-safe value: returns empty string for null/undefined */
function v(val) {
  return val == null ? "" : val;
}
/* Amount with thousand separators. Anything that is not a plain number is passed
   through untouched, so values like "10%" or an empty cell stay as they are.
   Latin digits are used on purpose, matching the printed design. */
/* Truthy across the shapes the callers use: true, "Y", "y", 1, "true". */
function isTrue(val) {
  if (val === true || val === 1) return true;
  const s = String(val == null ? "" : val).trim().toLowerCase();
  return s === "y" || s === "yes" || s === "true" || s === "1";
}
function money(val) {
  if (val == null) return "";

  const raw = String(val).trim();
  if (raw === "") return "";

  const n = Number(raw.replace(/,/g, ""));
  if (!isFinite(n)) return val;

  return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
}
/* ── Compound logos ──────────────────────────────────────────────
   Prints receive the compound by name, not by id, so the logo paths from
   T_GUEST_COMPOUNDS.LOGO_PATH are fetched once and matched on the name.
   Served from the same origin as the app, so the print window can load them.
   ──────────────────────────────────────────────────────────────── */
let compoundLogos = {};

function cacheCompoundLogos(rows) {
  const map = {};
  (rows || []).forEach((row) => {
    if (!row || !row.logo) return;
    if (row.label) map[String(row.label).trim().toLowerCase()] = row.logo;
    if (row.arLabel) map[String(row.arLabel).trim().toLowerCase()] = row.logo;
  });
  compoundLogos = map;
}

export function loadCompoundLogos() {
  try {
    return fetch("/api/data/GetAllCompoundsDropdown", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + sessionStorage.getItem("748784"),
      },
      body: "{}",
    })
      .then((res) => res.json())
      .then((json) => {
        cacheCompoundLogos(json && json.respData ? json.respData.data : []);
      })
      .catch(() => {
        /* printing must still work without the compound logo */
      });
  } catch (e) {
    return Promise.resolve();
  }
}

function compoundLogo(nameEn, nameAr) {
  const key = (n) => String(n || "").trim().toLowerCase();
  return compoundLogos[key(nameEn)] || compoundLogos[key(nameAr)] || "";
}
/* ─────────────────────────────────────────────────────────────── */


/* ── Receipt voucher layout ───────────────────────────────────────
   Shared bilingual (AR/EN) voucher used by every print in the system.
   Labels always show both languages, like the printed design, so the
   sheet reads the same whichever UI language produced it.
   ──────────────────────────────────────────────────────────────── */

const VOUCHER_CSS = `
  * { box-sizing: border-box; }
  body { font-family: "Segoe UI", Tahoma, Arial, sans-serif; color: #0b3b5a;
         margin: 0; padding: 16px; }
  .v-wrap { max-width: 920px; margin: 0 auto; }
  .v-head { display: flex; align-items: center; justify-content: space-between;
            gap: 12px; margin-bottom: 12px; }
  .v-head img { height: 64px; width: auto; max-width: 100%; object-fit: contain; }
  .v-head .side { width: 190px; font-size: 12px; font-weight: 700; text-align: center; }
  .v-head .side-name { padding-top: 20px; }
  .v-title { text-align: center; flex: 1; }
  .v-title .ar { font-size: 22px; font-weight: 700; }
  .v-title .en { font-size: 13px; font-weight: 600; letter-spacing: .4px; }
  table { width: 100%; border-collapse: collapse; }
  .v-top td { border: 1px solid #0b3b5a; padding: 6px 9px; font-size: 12px; width: 50%; }
  .v-section { background: #0b3b5a; color: #fff; padding: 5px 10px; font-size: 12px;
               font-weight: 700; display: flex; justify-content: space-between;
               margin-top: 12px; }
  .v-grid td { border: 1px solid #0b3b5a; padding: 6px 9px; font-size: 12px;
               vertical-align: top; }
  .v-grid .lbl { background: #eaf3f8; width: 22%; font-weight: 700; }
  .v-grid .lbl span { display: block; font-weight: 400; font-size: 10px; color: #4a7fa0; }
  .v-items th { background: #2e7fa8; color: #fff; border: 1px solid #0b3b5a;
                padding: 6px 8px; font-size: 11px; }
  .v-items th span { display: block; font-weight: 400; font-size: 10px; }
  .v-items td { border: 1px solid #0b3b5a; padding: 6px 8px; font-size: 12px;
                text-align: center; height: 26px; }
  .v-items .idx { background: #eaf3f8; font-weight: 700; width: 36px; }
  .v-total td { border: 1px solid #0b3b5a; padding: 6px 9px; font-size: 13px; font-weight: 700; }
  .v-total .lbl { background: #0b3b5a; color: #fff; text-align: center; width: 32%; }
  .v-notes { border: 1px solid #0b3b5a; border-top: 0; min-height: 64px;
             padding: 8px; font-size: 12px; }
  .v-sign { margin-top: 14px; display: flex; gap: 14px; }
  .v-sign .box { flex: 1; border: 1px solid #0b3b5a; }
  .v-sign .box .row { padding: 6px 9px; font-size: 12px; font-weight: 700; }
  .v-sign .box .name { min-height: 54px; border-bottom: 1px solid #0b3b5a; }
  .v-foot { margin-top: 14px; border-top: 3px solid #0b3b5a; padding-top: 6px;
            text-align: center; font-size: 12px; font-weight: 700; }
  .manual { color: #1b6ca8; font-weight: 700; font-size: 11px; }
  .v-items .note { text-align: right; font-size: 11px; word-break: break-word; }
  .v-items .paid { color: #155724; font-weight: 700; }
  .v-items .unpaid { color: #a3141f; font-weight: 700; }
  .chk { display: inline-block; width: 11px; height: 11px; border: 1px solid #0b3b5a;
         margin: 0 4px; vertical-align: middle; }
  .chk.on { background: #0b3b5a; }
  /* Browsers drop background colours when printing unless this is set, which is
     what left the section bands and table headers white on paper. */
  * { -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important; }

  @page { size: A4; margin: 10mm; }

  @media print {
    body { padding: 0; }
    .v-wrap { max-width: none; }
    /* Keep a voucher from splitting across pages mid-section. */
    .v-items tr, .v-sign, .v-total tr { page-break-inside: avoid; }
  }
`;

/* Bilingual label cell: Arabic on top, English underneath. */
function vLbl(ar, en) {
  return `${ar}<span>${en}</span>`;
}

/* One detail row cell pair. */
function vCell(item, span) {
  const colspan = span ? ` colspan="${span}"` : "";
  return `<td class="lbl">${vLbl(item.ar, item.en)}</td>` +
         `<td${colspan}>${item.manual ? "" : v(item.value)}</td>`;
}

/* Details grid: two label/value pairs per row, a lone trailing item spans the rest. */
function vDetails(details) {
  const rows = (details || []).filter(Boolean);
  let html = "";
  for (let i = 0; i < rows.length; i += 2) {
    html += `<tr>${vCell(rows[i])}${rows[i + 1] ? vCell(rows[i + 1]) : `<td colspan="2"></td>`}</tr>`;
  }
  return html;
}

/* Items table. Blank rows pad it out to keep the sheet a consistent height. */
/* Paid / Unpaid wording for a single item, coloured so the state is obvious on paper. */
function vStatus(paid) {
  return paid
    ? `<span class="paid">${tr("Paid", "مدفوعة")}</span>`
    : `<span class="unpaid">${tr("Unpaid", "غير مدفوعة")}</span>`;
}

function vItems(items, minRows, showStatus, showNotes) {
  const list = items || [];
  let html = "";

  list.forEach((it, i) => {
    html += `<tr>
      <td class="idx">${i + 1}</td>
      <td>${v(it.name)}</td>
      <td>${v(it.invoiceNo)}</td>
      <td>${v(it.dueDate)}</td>
      <td>${money(it.amount)}</td>
      ${showNotes ? `<td class="note">${v(it.notes)}</td>` : ""}
      ${showStatus ? `<td>${vStatus(isTrue(it.isPaid))}</td>` : ""}
    </tr>`;
  });

  for (let i = list.length; i < (minRows ?? 5); i++) {
    html += `<tr><td class="idx">${i + 1}</td><td></td><td></td><td></td><td></td>` +
            `${showNotes ? "<td></td>" : ""}` +
            `${showStatus ? "<td></td>" : ""}</tr>`;
  }

  return html;
}

/* True when at least one bill carries a note, so the column only appears when used. */
function anyNotes(items) {
  return (items || []).some((it) => it && String(it.notes || "").trim() !== "");
}

/* Payment method row with the used method ticked. */
function vPaymentMethod(method) {
  const m = (method || "").toString().toLowerCase();
  const isCash = m.includes("cash") || m.includes("نقد");
  const isElectronic = !!m && !isCash;

  return `<tr>
    <td class="lbl">${vLbl("طريقة الدفع", "Payment Method")}</td>
    <td colspan="3">
      <span class="chk ${isCash ? "on" : ""}"></span> ${tr("Cash", "نقداً")}
      &nbsp;&nbsp;&nbsp;
      <span class="chk ${isElectronic ? "on" : ""}"></span> ${tr("Electronic Payment", "الدفع الإلكتروني")}
      ${method ? `&nbsp;&nbsp;&nbsp;(${v(method)})` : ""}
    </td>
  </tr>`;
}

function renderVoucher(o) {
  const totalLabel = o.totalLabelAr
    ? vLbl(o.totalLabelAr, o.totalLabelEn)
    : vLbl("المجموع (IQD)", "Total (IQD)");

  // A full document with a real <head>: styles in a <header> element are body
  // content, which browsers treat less reliably when rendering for print.
  return `<!DOCTYPE html>
<html dir="${dir()}" lang="${getLang()}">
<head>
  <meta charset="utf-8" />
  <title>${tr(o.titleEn, o.titleAr)}</title>
  <style>${VOUCHER_CSS}</style>
</head>
<body>
  <div id="toPrint" class="v-wrap" dir="${dir()}">

    <div class="v-head">
      <div class="side">
        ${o.compoundLogo
          ? `<img src="${o.compoundLogo}" alt="${v(o.compound)}" />`
          : `<div class="side-name">${v(o.compound)}</div>`}
      </div>
      <div class="v-title">
        <div class="ar">${o.titleAr}</div>
        <div class="en">${o.titleEn}</div>
      </div>
      <div class="side"><img src="${logo}" alt="Modon" /></div>
    </div>

    <table class="v-top">
      <tr>
        <td><b>${tr("Voucher No.", "رقم السند")}</b> &nbsp; ${v(o.voucherNo)}</td>
        <td><b>${tr("Date", "التاريخ")}</b> &nbsp; ${v(o.date)}</td>
      </tr>
    </table>

    <div class="v-section"><span>بيانات السند</span><span>Voucher Details</span></div>
    <table class="v-grid">
      ${vDetails(o.details)}
      ${o.showPaymentMethod ? vPaymentMethod(o.paymentMethod) : ""}
      ${o.showPosRow ? `<tr>
        <td class="lbl">${vLbl("رقم العملية / إيصال POS", "Transaction / POS Receipt No.")}</td>
        <td colspan="3"></td>
      </tr>` : ""}
    </table>

    <div class="v-section"><span>البنود</span><span>Items</span></div>
    <table class="v-items">
      <tr>
        <th>#</th>
        <th>اسم الخدمة / البند<span>Service / Item</span></th>
        <th>رقم الفاتورة<span>Invoice No.</span></th>
        <th>تاريخ الاستحقاق<span>Due Date</span></th>
        <th>المبلغ (IQD)<span>Amount</span></th>
        ${o.showNotes ? `<th>الملاحظات<span>Notes</span></th>` : ""}
        ${o.showStatus ? `<th>الحالة<span>Status</span></th>` : ""}
      </tr>
      ${vItems(o.items, o.minRows, o.showStatus, o.showNotes)}
    </table>

    <table class="v-total">
      <tr>
        <td class="lbl">${totalLabel}</td>
        <td>${money(o.total)}</td>
      </tr>
      ${(o.extraTotals || []).map((x) =>
        `<tr><td class="lbl">${vLbl(x.ar, x.en)}</td><td>${money(x.value)}</td></tr>`).join("")}
    </table>

    <div class="v-section"><span>ملاحظات (اختياري)</span><span>Notes (Optional)</span></div>
    <div class="v-notes">${o.notes ? v(o.notes) : ""}</div>

    ${o.showSignatures === false ? "" : `<div class="v-sign">
      <div class="box">
        <div class="row name">${tr("Employee Name", "اسم الموظف")} &nbsp; ${v(o.employee)}</div>
        <div class="row">${tr("Employee Signature", "توقيع الموظف")}</div>
      </div>
      <div class="box">
        <div class="row name">${tr("Customer Name", "اسم الزبون")} &nbsp; ${v(o.customer)}</div>
        <div class="row">${tr("Customer Signature", "توقيع الزبون")}</div>
      </div>
    </div>`}

    <div class="v-foot">Modon Development</div>
  </div>
</body>
</html>`;
}

/* Printing too early gives a blank or half-drawn sheet: the embedded logo and the
   laid-out tables are not ready yet. Wait for the document to finish loading and
   for every image to settle, then print. The timeout is a backstop so a stalled
   image can never leave the window stuck without a print dialog. */
const PRINT_MAX_WAIT_MS = 3000;
const PRINT_SETTLE_MS = 400;

function whenReady(win) {
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };

    // Backstop first, so a failed image or a blocked load still prints.
    setTimeout(finish, PRINT_MAX_WAIT_MS);

    const waitForImages = () => {
      const images = Array.from(win.document.images || []);
      const pending = images.filter((img) => !img.complete);

      if (pending.length === 0) {
        finish();
        return;
      }

      let left = pending.length;
      const settle = () => {
        left -= 1;
        if (left <= 0) finish();
      };

      pending.forEach((img) => {
        img.addEventListener("load", settle, { once: true });
        img.addEventListener("error", settle, { once: true });
      });
    };

    if (win.document.readyState === "complete") {
      waitForImages();
    } else {
      win.addEventListener("load", waitForImages, { once: true });
      // readyState can reach complete without firing load for a written document.
      const poll = setInterval(() => {
        if (win.document.readyState === "complete") {
          clearInterval(poll);
          waitForImages();
        }
      }, 50);
      setTimeout(() => clearInterval(poll), PRINT_MAX_WAIT_MS);
    }
  });
}

function openPrint(titleEn, titleAr, build) {
  // Opened up front, while still inside the click handler, so popup blockers
  // do not reject it after the logo lookup.
  var myWindow = window.open("", tr(titleEn, titleAr), "");

  if (!myWindow) return; // popup blocked

  // The compound logo paths are needed before the markup is built. Fetched once
  // and reused; a failure here just prints without the compound logo.
  const logosReady = Object.keys(compoundLogos).length
    ? Promise.resolve()
    : loadCompoundLogos();

  return logosReady
    .then(() => {
      const html = typeof build === "function" ? build() : build;
      myWindow.document.write(html);
      myWindow.document.close();
      myWindow.focus();
      return whenReady(myWindow);
    })
    // A short settle lets the browser finish laying the tables out before the
    // print dialog freezes the page.
    .then(() => sleep(PRINT_SETTLE_MS))
    .then(() => {
      try {
        myWindow.focus();
        myWindow.print();
      } catch (e) {
        /* window closed before printing */
      }
    });
}

/* Detail rows shared by the unit-based vouchers. */
function unitDetails(customer, unitName, operator, extra) {
  return [
    { ar: "الزبون", en: "Customer", value: customer },
    { ar: "رقم هاتف الزبون", en: "Customer Phone", manual: true },
    { ar: "رقم الوحدة", en: "Unit Number", value: unitName },
    { ar: "اسم الموظف", en: "Employee Name", value: operator },
  ].concat(extra || []);
}

/* ── Prints ──────────────────────────────────────────────────────── */

export function buyVoucherPrint(
  paymentId,
  paymentMethod,
  unitName,
  ownerName,
  voucherType,
  price,
  serialNumber,
  payDate,
  operator = "",
  kwRate = "",
  usdAmount = ""
) {
  const titleAr = "سند قبض", titleEn = "Receipt Voucher";

  openPrint(titleEn, titleAr, () => renderVoucher({
    titleAr, titleEn,
    voucherNo: paymentId,
    date: payDate,
    details: unitDetails(ownerName, unitName, operator, [
      serialNumber ? { ar: "الرقم التسلسلي", en: "Serial Number", value: serialNumber } : null,
    ]),
    showPaymentMethod: true,
    paymentMethod,
    showPosRow: true,
    // Paid on the spot, so the receipt number doubles as the invoice number and
    // the bill it raises is due the same day.
    items: [{ name: voucherType || tr("Electricity Voucher", "قسيمة كهرباء"),
              invoiceNo: paymentId, dueDate: payDate, amount: price }],
    total: price,
    employee: operator,
    customer: ownerName,
  }));
}

export function electricityPrint(
  paymentId,
  paymentMethod,
  unitName,
  ownerName,
  voucherType,
  price,
  serialNumber,
  payDate,
  operator = "",
  kwRate = "",
  usdAmount = "",
  seqNo = "",
  compoundNameEn = "",
  compoundNameAr = ""
) {
  const titleAr = "سند قبض", titleEn = "Receipt Voucher";
  const kw = !serialNumber && !voucherType ? usdAmount / (kwRate ?? 0) : "";

  openPrint(titleEn, titleAr, () => renderVoucher({
    titleAr, titleEn,
    voucherNo: seqNo || paymentId,
    date: payDate,
    compound: tr(v(compoundNameEn), v(compoundNameAr)),
    compoundLogo: compoundLogo(compoundNameEn, compoundNameAr),
    details: unitDetails(ownerName, unitName, operator, [
      serialNumber ? { ar: "الرقم التسلسلي", en: "Serial Number", value: serialNumber } : null,
      kw ? { ar: "الكمية (KW)", en: "Quantity (KW)", value: money(kw) } : null,
    ]),
    showPaymentMethod: true,
    paymentMethod,
    showPosRow: true,
    items: [{ name: voucherType || tr("Electricity", "كهرباء"),
              invoiceNo: seqNo || paymentId, dueDate: payDate, amount: price }],
    total: price,
    employee: operator,
    customer: ownerName,
  }));
}

export function internetPrint(
  paymentId,
  paymentMethod,
  unitName,
  ownerName,
  voucherType,
  price,
  serialNumber,
  payDate,
  operator = "",
  kwRate = "",
  usdAmount = "",
  seqNo = "",
  compoundNameEn = "",
  compoundNameAr = ""
) {
  const titleAr = "سند قبض", titleEn = "Receipt Voucher";

  openPrint(titleEn, titleAr, () => renderVoucher({
    titleAr, titleEn,
    voucherNo: seqNo || paymentId,
    date: payDate,
    compound: tr(v(compoundNameEn), v(compoundNameAr)),
    compoundLogo: compoundLogo(compoundNameEn, compoundNameAr),
    details: unitDetails(ownerName, unitName, operator, [
      serialNumber ? { ar: "الرقم التسلسلي", en: "Serial Number", value: serialNumber } : null,
    ]),
    showPaymentMethod: true,
    paymentMethod,
    showPosRow: true,
    items: [{ name: voucherType || tr("Internet", "إنترنت"),
              invoiceNo: seqNo || paymentId, dueDate: payDate, amount: price }],
    total: price,
    employee: operator,
    customer: ownerName,
  }));
}

export function otherPaymentPrint(
  paymentId,
  paymentMethod,
  unitName,
  ownerName,
  price,
  service,
  payDate,
  operator,
  notes,
  seqNo = "",
  compoundNameEn = "",
  compoundNameAr = ""
) {
  const titleAr = "سند قبض", titleEn = "Receipt Voucher";

  openPrint(titleEn, titleAr, () => renderVoucher({
    titleAr, titleEn,
    voucherNo: seqNo || paymentId,
    date: payDate,
    compound: tr(v(compoundNameEn), v(compoundNameAr)),
    compoundLogo: compoundLogo(compoundNameEn, compoundNameAr),
    details: unitDetails(ownerName, unitName, operator),
    showPaymentMethod: true,
    paymentMethod,
    showPosRow: true,
    items: [{ name: service, amount: price }],
    total: price,
    notes,
    employee: operator,
    customer: ownerName,
  }));
}

export function payBillPrint(
  bills,
  total,
  operator,
  discountType,
  discountAmount,
  paidAmount,
  paymentSeqNo = "",
  compoundNameEn = "",
  compoundNameAr = ""
) {
  const titleAr = "سند قبض", titleEn = "Receipt Voucher";
  const first = (bills && bills[0]) || {};
  const discount = v(discountAmount) + (discountType === "Percentage" ? "%" : "");

  const billItems = (bills || []).map((b) => ({
    name: b.SERVICE_NAME,
    invoiceNo: b.SEQ_NO,
    dueDate: b.DUE_DATE,
    amount: b.DUE_AMOUNT,
    notes: b.NOTES,
  }));

  openPrint(titleEn, titleAr, () => renderVoucher({
    titleAr, titleEn,
    // Some callers print straight after paying and have no payment sequence number
    // or pay date to hand, so fall back to what the bill itself carries.
    voucherNo: paymentSeqNo || first.SEQ_NO,
    date: first.PAY_DATE || first.DUE_DATE,
    compound: tr(v(compoundNameEn), v(compoundNameAr)),
    compoundLogo: compoundLogo(compoundNameEn, compoundNameAr),
    details: unitDetails(first.OWNER_NAME, first.UNIT_NAME, operator),
    showPaymentMethod: true,
    paymentMethod: first.METHOD_EN,
    showPosRow: true,
    items: billItems,
    showNotes: anyNotes(billItems),
    total,
    extraTotals: [
      discountType ? { ar: "نوع الخصم", en: "Discount Type", value: discountType } : null,
      discountType ? { ar: "قيمة الخصم", en: "Discount Value", value: discount } : null,
      { ar: "الصافي (IQD)", en: "Net Amount (IQD)", value: paidAmount },
    ].filter(Boolean),
    employee: operator,
    customer: first.OWNER_NAME,
  }));
}

export function expensePrint(data, operator) {
  // Money paid out, so this one is a payment voucher rather than a receipt.
  const titleAr = "سند صرف", titleEn = "Payment Voucher";

  openPrint(titleEn, titleAr, () => renderVoucher({
    titleAr, titleEn,
    voucherNo: data.ID,
    date: data.DATE,
    details: [
      { ar: "نوع المصروف", en: "Expense Type", value: data.EXPENSE_TYPE },
      { ar: "اسم الموظف", en: "Employee Name", value: operator },
      { ar: "تاريخ الإدخال", en: "Insert Date", value: data.INSERT_DATE },
    ],
    showPosRow: false,
    items: [{ name: data.TITLE, amount: data.AMOUNT }],
    total: data.AMOUNT,
    notes: data.DETAILS,
    employee: operator,
    customer: "",
  }));
}

export function incomePrint(data, operator) {
  const titleAr = "سند قبض", titleEn = "Receipt Voucher";

  openPrint(titleEn, titleAr, () => renderVoucher({
    titleAr, titleEn,
    voucherNo: data.ID,
    date: data.DATE,
    details: [
      { ar: "نوع الإيراد", en: "Income Type", value: data.INCOME_TYPE },
      { ar: "اسم الموظف", en: "Employee Name", value: operator },
      { ar: "تاريخ الإدخال", en: "Insert Date", value: data.INSERT_DATE },
    ],
    showPosRow: false,
    items: [{ name: data.TITLE, amount: data.AMOUNT }],
    total: data.AMOUNT,
    notes: data.DETAILS,
    employee: operator,
    customer: "",
  }));
}

export function postpaidBillPrint(
  billId,
  unitName,
  ownerName,
  serviceName,
  dueDate,
  dueAmount,
  notes,
  operator,
  isPaid = "",
  payDate = "",
  seqNo = "",
  compoundNameEn = "",
  compoundNameAr = ""
) {
  // A bill rather than money received, so no receipt wording. Once it has been
  // paid the voucher shows the payment date and drops the "amount due" wording.
  // Callers are inconsistent: the dashboard passes a boolean (IS_PAID === "Y"),
  // others pass the raw "Y"/"N" flag, so accept either.
  const paid = isTrue(isPaid);
  const titleAr = "فاتورة", titleEn = "Bill";

  openPrint(titleEn, titleAr, () => renderVoucher({
    titleAr, titleEn,
    voucherNo: seqNo || billId,
    date: paid ? payDate : dueDate,
    compound: tr(v(compoundNameEn), v(compoundNameAr)),
    compoundLogo: compoundLogo(compoundNameEn, compoundNameAr),
    details: unitDetails(ownerName, unitName, operator, [
      { ar: "الحالة", en: "Status", value: paid ? tr("Paid", "مدفوعة") : tr("Unpaid", "غير مدفوعة") },
      paid && payDate ? { ar: "تاريخ الدفع", en: "Pay Date", value: payDate } : null,
    ]),
    showPaymentMethod: false,
    showPosRow: false,
    showStatus: true,
    // The bill's own note sits on its row, like the other bill listings. The box at
    // the bottom is then left empty to write in.
    showNotes: String(notes || "").trim() !== "",
    items: [{ name: serviceName, invoiceNo: seqNo || billId, dueDate,
              amount: dueAmount, isPaid: paid, notes }],
    total: dueAmount,
    totalLabelAr: paid ? "المبلغ المدفوع (IQD)" : "المبلغ المستحق (IQD)",
    totalLabelEn: paid ? "Amount Paid (IQD)" : "Amount Due (IQD)",
    employee: operator,
    customer: ownerName,
  }));
}

export function otherPaymentMultiPrint(
  seqNo,
  compoundNameEn,
  compoundNameAr,
  unitName,
  ownerName,
  operator,
  isPostpaid,
  paymentMethod,
  payDate,
  bills,
  totalAmount
) {
  const titleAr = isPostpaid ? "فاتورة" : "سند قبض";
  const titleEn = isPostpaid ? "Bill" : "Receipt Voucher";
  const firstBill = (bills && bills[0]) || {};

  const multiItems = (bills || []).map((b) => ({
    name: b.service,
    invoiceNo: b.seqNo,
    dueDate: b.dueDate,
    amount: b.amount,
    isPaid: b.isPaid,
    notes: b.notes,
  }));

  openPrint(titleEn, titleAr, () => renderVoucher({
    titleAr, titleEn,
    // Issuing unpaid bills leaves the payment sequence number and pay date empty,
    // so the bill's own number and due date stand in.
    voucherNo: seqNo || firstBill.seqNo,
    date: payDate || firstBill.dueDate,
    compound: tr(v(compoundNameEn), v(compoundNameAr)),
    compoundLogo: compoundLogo(compoundNameEn, compoundNameAr),
    details: unitDetails(ownerName, unitName, operator),
    showPaymentMethod: !isPostpaid,
    paymentMethod,
    showPosRow: !isPostpaid,
    showStatus: isPostpaid,
    items: multiItems,
    showNotes: anyNotes(multiItems),
    total: totalAmount,
    totalLabelAr: isPostpaid ? "المبلغ المستحق (IQD)" : "المجموع (IQD)",
    totalLabelEn: isPostpaid ? "Amount Due (IQD)" : "Total (IQD)",

    employee: operator,
    customer: ownerName,
  }));
}

export function unitInstallmentPrint(bills, unit, operator) {
  const titleAr = "جدول الأقساط", titleEn = "Installment Schedule";
  const list = bills || [];

  openPrint(titleEn, titleAr, () => renderVoucher({
    titleAr, titleEn,
    voucherNo: "",
    date: "",
    details: unitDetails(unit.OWNER_NAME, unit.UNIT_NAME, operator, [
      { ar: "سعر الوحدة", en: "Unit Price", value: money(unit.PRICE) },
      { ar: "المبلغ المتبقي", en: "Remaining Amount", value: money(unit.REMAINING_AMOUNT) },
    ]),
    showPaymentMethod: false,
    showPosRow: false,
    items: list.map((b, i) => ({
      name: `${tr("Installment", "قسط")} ${i + 1} — ` +
            (isTrue(b.IS_PAID) ? tr("Paid", "مدفوع") : tr("Unpaid", "غير مدفوع")) +
            (b.PAY_DATE ? ` (${v(b.PAY_DATE)})` : ""),
      dueDate: b.DUE_DATE,
      amount: b.AMOUNT,
    })),
    minRows: Math.max(list.length, 5),
    total: list.reduce((sum, b) => sum + (parseFloat(b.AMOUNT) || 0), 0),
    employee: operator,
    customer: unit.OWNER_NAME,
  }));
}

export function installmentPrint(bill, unitName, ownerName, operator) {
  const titleAr = "سند قبض", titleEn = "Receipt Voucher";

  openPrint(titleEn, titleAr, () => renderVoucher({
    titleAr, titleEn,
    voucherNo: bill.ID,
    date: bill.PAY_DATE || bill.DUE_DATE,
    details: unitDetails(ownerName, unitName, operator),
    showPaymentMethod: true,
    showPosRow: true,
    items: [{
      name: tr("Installment", "قسط") +
            (isTrue(bill.IS_PAID) ? ` — ${tr("Paid", "مدفوع")}` : ""),
      dueDate: bill.DUE_DATE,
      amount: bill.AMOUNT,
    }],
    total: bill.AMOUNT,
    employee: operator,
    customer: ownerName,
  }));
}

function sleep(time) {
  return new Promise((resolve) => setTimeout(resolve, time));
}
